import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useUser, useUpdateUser } from '../hooks/useUsers';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { GoogleAddressAutocomplete } from '../components/common/GoogleAddressAutocomplete';
import { NotificationSettings } from '../components/messaging/NotificationSettings';
import { SavedRepliesSection } from '../components/messaging/SavedRepliesSection';
import { DailyTaskEmailSetting } from '../components/tasks/DailyTaskEmailSetting';
import { TaskCalendarFeedSetting } from '../components/tasks/TaskCalendarFeedSetting';
import { SystemRole, hasMinimumRole } from '@peles/shared';
import { PhoneInput } from '../components/common/PhoneInput';

interface ProfileForm {
  name: string;
  phone: string;
  address: string;
}

export function ProfilePage() {
  const { user: authUser } = useAuth();
  const { data: user, isLoading } = useUser(authUser?.id || '');
  const updateUser = useUpdateUser();

  const [form, setForm] = useState<ProfileForm>({ name: '', phone: '', address: '' });
  const [originalForm, setOriginalForm] = useState<ProfileForm>(form);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (user) {
      const state: ProfileForm = {
        name: user.name,
        phone: user.phone || '',
        address: user.address || '',
      };
      setForm(state);
      setOriginalForm(state);
    }
  }, [user]);

  const dirty =
    form.name !== originalForm.name ||
    form.phone !== originalForm.phone ||
    form.address !== originalForm.address;

  useUnsavedChangesGuard(dirty);

  const handleSave = useCallback(() => {
    if (!authUser) return;

    const updates: Record<string, unknown> = {};
    if (form.name !== originalForm.name) updates.name = form.name;
    if (form.phone !== originalForm.phone) updates.phone = form.phone || null;
    if (form.address !== originalForm.address) updates.address = form.address || null;

    updateUser.mutate(
      { id: authUser.id, updates },
      {
        onSuccess: (updated) => {
          const state: ProfileForm = {
            name: updated.name,
            phone: updated.phone || '',
            address: updated.address || '',
          };
          setForm(state);
          setOriginalForm(state);
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        },
      },
    );
  }, [form, originalForm, authUser, updateUser]);

  const handleCancel = useCallback(() => {
    setForm(originalForm);
  }, [originalForm]);

  if (isLoading) {
    return <p className="text-gray-500">Loading profile...</p>;
  }

  if (!user) {
    return <p className="text-gray-500">Profile not found</p>;
  }

  return (
    <div className="max-w-xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">My Profile</h1>

      {saved && (
        <div className="mb-4 px-3 py-2 bg-green-50 text-green-700 text-sm rounded">
          Profile updated successfully
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div>
          <label htmlFor="profile-name" className="block text-sm font-medium text-gray-700 mb-1">
            Name
          </label>
          <input
            id="profile-name"
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <p className="text-sm text-gray-600 px-3 py-2 bg-gray-50 rounded-md">{user.email}</p>
        </div>

        <div>
          <label htmlFor="profile-phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone
          </label>
          <PhoneInput
            id="profile-phone"
            value={form.phone}
            onChange={(phone) => setForm((f) => ({ ...f, phone }))}
            placeholder="Enter phone number"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label htmlFor="profile-address" className="block text-sm font-medium text-gray-700 mb-1">
            Address
          </label>
          <GoogleAddressAutocomplete
            id="profile-address"
            value={form.address}
            onChange={(v) => setForm((f) => ({ ...f, address: v }))}
            onAddressSelect={(fields) =>
              setForm((prev) => ({ ...prev, address: fields.formattedAddress }))
            }
            placeholder="Enter address"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <button
            onClick={handleSave}
            disabled={!dirty || updateUser.isPending}
            className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updateUser.isPending ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            onClick={handleCancel}
            disabled={!dirty}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* A vendor reads no tasks anywhere, so has none to be emailed or subscribe to. */}
      {user.systemRole !== SystemRole.VENDOR && (
        <>
          <div className="mt-6">
            <DailyTaskEmailSetting user={user} />
          </div>

          <div className="mt-6">
            <TaskCalendarFeedSetting user={user} />
          </div>
        </>
      )}

      <div className="mt-6">
        <NotificationSettings userId={user.id} />
      </div>

      {authUser && hasMinimumRole(authUser.systemRole, SystemRole.PLANNER) && (
        <div className="mt-6">
          <SavedRepliesSection />
        </div>
      )}
    </div>
  );
}
